import { useTranslation } from "react-i18next";
import { ConsentStatusTypes } from "../constant/filters";
import { CONSENT_STATUS_TYPES, CONSENT_STATUS_COLORS } from "../constant/consent";
import { formatConsentEventDate, getConsentActionUserName } from "../utils/eConsentHelper";

/**
 * Maps backend latestConsentEventType to display status type
 * @param {string} eventType - The latestConsentEventType from backend
 * @returns {string} The display status type
 */
export const mapEventTypeToDisplayStatus = (eventType) => {
  if (!eventType) {
    return CONSENT_STATUS_TYPES.NO_RECORD;
  }

  const statusMap = {
    [ConsentStatusTypes.CONSENT_ACCEPTED]: CONSENT_STATUS_TYPES.RECEIVED,
    [ConsentStatusTypes.CONSENT_REVOKED]: CONSENT_STATUS_TYPES.REVOKED,
    [ConsentStatusTypes.CONSENT_EXPIRED]: CONSENT_STATUS_TYPES.EXPIRED,
    [ConsentStatusTypes.LINK_SENT]: CONSENT_STATUS_TYPES.PENDING,
    [ConsentStatusTypes.LINK_EXPIRED]: CONSENT_STATUS_TYPES.LINK_EXPIRED,
    [ConsentStatusTypes.LINK_REVOKED]: CONSENT_STATUS_TYPES.LINK_EXPIRED
  };

  return statusMap[eventType] || CONSENT_STATUS_TYPES.NO_RECORD;
};

/**
 * Hook to get consent status configuration
 * @param {string} status - The consent status type
 * @param {Object} consentData - Optional consent data for tooltips (createdAt, user info)
 * @returns {Object} Configuration with text, dotColor, and optional tooltip
 */
export const useConsentStatusConfig = (status, consentData = null) => {
  const { t } = useTranslation();

  const getConfig = () => {
    const consentUserName = consentData ? getConsentActionUserName(consentData, t) : null;
    const formattedDate = consentData?.createdAt ? formatConsentEventDate(consentData.createdAt) : null;

    switch (status) {
      case CONSENT_STATUS_TYPES.NO_RECORD:
        return {
          text: t("consent.status.no_record"),
          dotColor: CONSENT_STATUS_COLORS[CONSENT_STATUS_TYPES.NO_RECORD],
          tooltip: null
        };
      case CONSENT_STATUS_TYPES.PENDING:
        return {
          text: t("consent.status.pending_signature"),
          dotColor: CONSENT_STATUS_COLORS[CONSENT_STATUS_TYPES.PENDING],
          tooltip: consentData
            ? t("consent.tooltips.pending", {
                user: consentUserName,
                date: formattedDate
              })
            : null
        };
      case CONSENT_STATUS_TYPES.RECEIVED:
        return {
          text: t("consent.status.consent_received"),
          dotColor: CONSENT_STATUS_COLORS[CONSENT_STATUS_TYPES.RECEIVED],
          tooltip: consentData
            ? t("consent.tooltips.received", {
                user: consentUserName,
                date: formattedDate
              })
            : null
        };
      case CONSENT_STATUS_TYPES.REVOKED:
        return {
          text: t("consent.status.consent_revoked"),
          dotColor: CONSENT_STATUS_COLORS[CONSENT_STATUS_TYPES.REVOKED],
          tooltip: consentData
            ? t("consent.tooltips.revoked", {
                user: consentUserName,
                date: formattedDate
              })
            : null
        };
      case CONSENT_STATUS_TYPES.EXPIRED:
        return {
          text: t("consent.status.consent_expired"),
          dotColor: CONSENT_STATUS_COLORS[CONSENT_STATUS_TYPES.EXPIRED],
          tooltip: consentData
            ? t("consent.tooltips.expired_by_system", {
                date: formattedDate
              })
            : null
        };
      case CONSENT_STATUS_TYPES.LINK_EXPIRED:
        return {
          text: t("consent.status.request_link_expired"),
          dotColor: CONSENT_STATUS_COLORS[CONSENT_STATUS_TYPES.LINK_EXPIRED],
          tooltip: consentData
            ? t("consent.tooltips.link_expired", {
                date: formattedDate
              })
            : null
        };
      default:
        return {
          text: t("consent.status.no_record"),
          dotColor: CONSENT_STATUS_COLORS[CONSENT_STATUS_TYPES.NO_RECORD],
          tooltip: null
        };
    }
  };

  return getConfig();
};
